import { useState, useCallback, useEffect, useRef, useMemo } from "react";
import { LOYALTY_API_BASE, ROWS_PER_PAGE } from "../constants";
import { loyaltyFetch } from "./loyaltyFetch";
import {
    buildSegmentsListUrl,
    parseSegmentsListResponse,
    buildSegmentListQueryParams,
    hasActiveSegmentListFilters,
    DEFAULT_SEGMENT_LIST_FILTERS,
    DEFAULT_SEGMENT_SORT,
} from "../components/segments/segmentListApi";
import { segmentErrorMessage } from "../components/segments/segmentErrorMessage";

const SEARCH_DEBOUNCE_MS = 350;

/**
 * Liste segments : pagination + filtres + tri serveur (répétés sur chaque page).
 */
export function useSegmentsList(brandName, { pageSize = ROWS_PER_PAGE } = {}) {
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [unomiSync, setUnomiSync] = useState(null);
    const [appliedFilters, setAppliedFilters] = useState(null);
    const [appliedSort, setAppliedSort] = useState(null);

    const [listFilters, setListFilters] = useState(DEFAULT_SEGMENT_LIST_FILTERS);
    const [sortBy, setSortBy] = useState(DEFAULT_SEGMENT_SORT);
    const [debouncedSearch, setDebouncedSearch] = useState("");

    const abortRef = useRef(null);
    const queryParamsRef = useRef({});
    const fetchGenerationRef = useRef(0);

    const queryParams = useMemo(
        () => buildSegmentListQueryParams({
            ...listFilters,
            search: debouncedSearch,
            sortBy,
        }),
        [
            listFilters.activeFilter,
            listFilters.kindFilter,
            listFilters.providerFilter,
            debouncedSearch,
            sortBy,
        ],
    );

    const hasServerFilters = hasActiveSegmentListFilters(queryParams);

    useEffect(() => {
        const timer = setTimeout(
            () => setDebouncedSearch(listFilters.search.trim()),
            SEARCH_DEBOUNCE_MS,
        );
        return () => clearTimeout(timer);
    }, [listFilters.search]);

    useEffect(() => {
        queryParamsRef.current = queryParams;
    }, [queryParams]);

    const fetchPage = useCallback(async (pageIndex, { forceSync = false, params } = {}) => {
        if (!brandName) return null;

        if (abortRef.current) abortRef.current.abort();
        const controller = new AbortController();
        abortRef.current = controller;

        const effectiveParams = params ?? queryParamsRef.current;
        const offset = pageIndex * pageSize;
        const generation = fetchGenerationRef.current + 1;
        fetchGenerationRef.current = generation;
        const path = buildSegmentsListUrl({
            brandName,
            limit: pageSize,
            offset,
            forceSync,
            queryParams: effectiveParams,
        });

        setLoading(true);
        setError(null);

        try {
            const res = await loyaltyFetch(`${LOYALTY_API_BASE}${path}`, {
                headers: { "X-Brand": brandName },
                signal: controller.signal,
            });
            const json = await res.json().catch(() => null);
            if (!res.ok) {
                throw new Error(segmentErrorMessage(res.status, json?.detail, "Liste des segments"));
            }
            if (generation !== fetchGenerationRef.current) return null;

            const parsed = parseSegmentsListResponse(json, pageSize, offset);
            setItems(parsed.items);
            setTotal(parsed.total);
            setPage(pageIndex);
            setAppliedFilters(parsed.filters);
            setAppliedSort(parsed.sort);
            setUnomiSync(res.headers.get("X-Unomi-Sync"));
            return parsed;
        } catch (err) {
            if (err.name === "AbortError") return null;
            setError(err.message);
            return null;
        } finally {
            if (abortRef.current === controller) {
                setLoading(false);
            }
        }
    }, [brandName, pageSize]);

    useEffect(() => {
        setListFilters(DEFAULT_SEGMENT_LIST_FILTERS);
        setSortBy(DEFAULT_SEGMENT_SORT);
        setDebouncedSearch("");
    }, [brandName]);

    useEffect(() => {
        setPage(0);
        fetchPage(0, { params: queryParams });
        return () => {
            if (abortRef.current) abortRef.current.abort();
        };
    }, [brandName, queryParams, fetchPage]);

    const goToPage = useCallback((pageIndex) => {
        fetchPage(pageIndex, { params: queryParamsRef.current });
    }, [fetchPage]);

    const refetch = useCallback(
        () => fetchPage(page, { forceSync: page === 0, params: queryParamsRef.current }),
        [fetchPage, page],
    );

    const refetchFromStart = useCallback(
        (opts = {}) => fetchPage(0, {
            forceSync: opts.forceSync !== false,
            params: queryParamsRef.current,
        }),
        [fetchPage],
    );

    const updateListFilters = useCallback((patch) => {
        setListFilters((prev) => ({ ...prev, ...patch }));
    }, []);

    const updateListSort = useCallback((nextSort) => {
        setSortBy(nextSort);
    }, []);

    return {
        items,
        total,
        page,
        pageSize,
        loading,
        error,
        unomiSync,
        listFilters,
        sortBy,
        updateListFilters,
        updateListSort,
        hasServerFilters,
        appliedFilters,
        appliedSort,
        goToPage,
        refetch,
        refetchFromStart,
        data: items,
    };
}
